import { useEffect, useRef, useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import { XCircleIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogOverlay } from '@reach/dialog';
import { motion } from 'framer-motion';

import { ChromeColoredIcon, ChromeIcon } from '@/components/icons/ElementIcons';
import WalletComparison from '@/components/shared/WalletComparison';
import { FAQ } from '@/components/shared/lp/developer/FAQ';

import KomodoWalletDownloads from '@/data/KomodoWalletDownloads.json';
import { siteData } from '@/data/configs/site';
import ellipseDownload from '@/public/images/lp/downloads/ellipse-download.svg';
import laptopMobile1 from '@/public/images/lp/downloads/laptop-mobile-1.png';
import laptopMobile from '@/public/images/lp/downloads/laptop-mobile.png';
import link from '@/public/images/lp/downloads/link.svg';
import walletCompare from '@/public/images/lp/downloads/wallet-compare.png';
import android from '@/public/images/lp/komodo-wallet/android.svg';
import ios from '@/public/images/lp/komodo-wallet/ios.svg';
import linux from '@/public/images/lp/komodo-wallet/linux.svg';
import macos from '@/public/images/lp/komodo-wallet/macos.svg';
import windows from '@/public/images/lp/komodo-wallet/windows.svg';
import androidqr from '@/public/images/lp/mobile/android-qr.png';
import iosqr from '@/public/images/lp/mobile/ios-qr.png';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';

export default function Downloads() {
  const [selected, setSelected] = useState(null);
  const [isDesktopModalOpen, setIsDesktopModalOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [isWebModalOpen, setIsWebModalOpen] = useState(false);

  const desktopModalRef = useRef(null);
  const mobileModalRef = useRef(null);
  const webModalRef = useRef(null);

  useEffect(() => {
    const closeModalIfClickOutside = (event) => {
      if (desktopModalRef.current && !desktopModalRef.current.contains(event.target)) {
        closeDesktopModal();
      }
      if (mobileModalRef.current && !mobileModalRef.current.contains(event.target)) {
        closeMobileModal();
      }
      if (webModalRef.current && !webModalRef.current.contains(event.target)) {
        closeWebModal();
      }
    };
    document.addEventListener('mousedown', closeModalIfClickOutside);

    return () => {
      document.removeEventListener('mousedown', closeModalIfClickOutside);
    };
  }, []);

  function openDesktopModal() {
    setIsDesktopModalOpen(true);
  }

  function closeDesktopModal() {
    setIsDesktopModalOpen(false);
  }

  function openMobileModal() {
    setIsMobileModalOpen(true);
  }

  function closeMobileModal() {
    setIsMobileModalOpen(false);
  }

  function openWebModal() {
    setIsWebModalOpen(true);
  }

  function closeWebModal() {
    setIsWebModalOpen(false);
  }

  const faqData = [
    {
      faqNo: '1',
      faqQstn: ' What is Komodo Wallet?',
      faqAnswer: (
        <span>
          Komodo Wallet is a non-custodial, multi-coin wallet that allows you to securely store,
          send, and receive cryptocurrencies across multiple blockchain networks.
        </span>
      ),
    },
    {
      faqNo: '2',
      faqQstn: ' Which operating systems does Komodo Wallet support?',
      faqAnswer: (
        <span>
          Komodo Wallet is available on Windows, macOS, Linux, Android, and iOS. You can also access
          it through a web app and a browser extension.
        </span>
      ),
    },
    {
      faqNo: '3',
      faqQstn: ' Is Komodo Wallet safe to use?',
      faqAnswer: (
        <span>
          Yes, Komodo Wallet is non-custodial, meaning you have full control over your private keys.
          Your data stays on your device, and Komodo team members will never ask for your private
          keys or seed phrase.
        </span>
      ),
    },
    {
      faqNo: '4',
      faqQstn: ' How do I back up my Komodo Wallet?',
      faqAnswer: (
        <span>
          When you create a wallet, you will receive a seed phrase. For the mobile app, you will
          receive a 12-word seed phrase. For the web app, browser extension, and desktop versions,
          you will receive a 24-word seed phrase. Write it down and store it securely, as this is
          the only way to recover your funds if you lose access to your device.
        </span>
      ),
    },
    {
      faqNo: '5',
      faqQstn: ' Does Komodo Wallet support NFTs?',
      faqAnswer: (
        <span>
          Yes. Komodo Wallet (web) and Komodo Wallet (browser extension) allow you to store, send,
          and receive NFTs on Ethereum, Polygon, BNB Chain, Avalanche, and Fantom.
        </span>
      ),
    },
    {
      faqNo: '6',
      faqQstn: ' Is there a fee to use Komodo Wallet?',
      faqAnswer: (
        <span>
          Komodo Wallet is free to download and use. You only pay blockchain network fees when
          sending transactions.
        </span>
      ),
    },
    {
      faqNo: '7',
      faqQstn: ' Which cryptocurrencies does Komodo Wallet support?',
      faqAnswer: (
        <span>
          Komodo Wallet supports a wide range of cryptocurrencies, including Bitcoin (BTC), Ethereum
          (ETH), Komodo (KMD), and many others. It also supports tokens from multiple blockchain
          networks. Check the wallet for the latest supported assets.
        </span>
      ),
    },
    {
      faqNo: '8',
      faqQstn: ' Can I buy crypto with fiat using Komodo Wallet?',
      faqAnswer: (
        <span>
          Yes. Komodo Wallet integrates with external providers that allow you to purchase crypto directly within the wallet. Depending on your location, you can pay with a credit card, debit card, or bank transfer.
          <br />
          Please note that availability varies by region, and each provider operates under separate terms and conditions.
        </span>
      ),
    },
    {
      faqNo: '9',
      faqQstn: ' How do I update Komodo Wallet?',
      faqAnswer: (
        <span>
          For desktop and mobile versions, download the latest version from the official Komodo
          Wallet website or your device&apos;s app store. The web app and browser extension update
          automatically, ensuring you always use the latest version.
        </span>
      ),
    },
    {
      faqNo: '10',
      faqQstn: (
        <span>
          What should I do if I forget my wallet password or lose access to my device and can&apos;t
          find my seed phrase?
        </span>
      ),
      faqAnswer: (
        <span>
          If you lose your seed phrase, there is no way to recover your wallet or funds. Always back
          up your seed phrase and store it securely in a safe location.
        </span>
      ),
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <>
      <main className='relative z-[100] overflow-hidden pt-[90px] font-sans text-white lg:pt-[100px]'>
        {/* Background Ellipses with animations */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.65 }}
          transition={{ duration: 1.5 }}
          className='absolute left-[-700px] top-[180px] z-[-10] h-[365px] w-[802px] bg-gradient-to-br from-black via-[#71A5FF] blur-[125px] md:left-[-600px] 2xl:left-[-400px]'
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.65 }}
          transition={{ duration: 1.5 }}
          className='absolute right-[-400px] top-[5px] z-[-10] h-[365px] w-[545px] bg-gradient-to-br from-black via-[#71A5FF] blur-[125px] md:right-[-350px] 2xl:right-[-200px] 2xl:top-[10px]'
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.65 }}
          transition={{ duration: 1.5 }}
          className='absolute left-[-10px] top-[1800px] z-[-10] h-[531px] w-[336px] -translate-x-1/2 bg-gradient-to-br from-black via-[#71A5FF] blur-[125px]'
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.65 }}
          transition={{ duration: 1.5 }}
          className='absolute right-[-330px] top-[1880px] z-[-10] h-[531px] w-[499px] bg-gradient-to-br from-black via-[#71A5FF] blur-[125px]'
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.55 }}
          transition={{ duration: 1.5 }}
          className='absolute left-[-300px] top-[2800px] z-[-10] h-[775px] w-[687px] bg-gradient-to-br from-black via-[#71A5FF] blur-[150px]'
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.55 }}
          transition={{ duration: 1.5 }}
          className='absolute right-[-250px] top-[2900px] z-[-10] h-[912px] w-[518px] bg-gradient-to-br from-black via-[#71A5FF] blur-[150px]'
        />

        <div className='container mx-auto px-4 xs:px-8 lg:grid lg:grid-cols-24 lg:gap-0 xl:px-0'>
          <div className='hidden lg:col-span-2 lg:block'></div>
          <div className='relative mx-auto w-full px-4 sm:px-8 lg:col-span-20 xl:px-0'>
            <motion.section
              initial='hidden'
              animate='visible'
              variants={staggerContainer}
              className='mx-auto max-w-[1010px] pt-[100px]'
            >
              <motion.h1
                variants={fadeInUp}
                className='text-center font-heading text-[40px] font-semibold leading-[45px] tracking-[0.01em] text-white lg:text-[64px] lg:leading-[70px] lg:tracking-[-0.01em]'
              >
                Download <span className='font-normal'>Komodo Wallet today and </span>
                <span className='font-semibold'>join the movement.</span>
              </motion.h1>
              <motion.p
                variants={fadeInUp}
                className='pt-6 text-center font-heading text-[24px] font-normal leading-[36px] tracking-[0.01em] text-white'
              >
                Eliminate the middleman and{' '}
                <span className='font-heading text-[24px] font-semibold leading-[36px] text-white'>
                  take real control
                </span>{' '}
                of what you own.
              </motion.p>
              <motion.div
                variants={staggerContainer}
                initial='hidden'
                animate='visible'
                className='mx-auto flex flex-col items-center justify-center gap-[30px] pt-[40px] lg:flex-row lg:gap-[50px] 2xl:gap-[102px]'
              >
                <motion.div variants={fadeInUp}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type='button'
                    className='downloads__desktop-wallet-btn inline-flex h-[58px] w-[250px] items-center justify-center p-[13px] text-center'
                    aria-expanded='false'
                    onClick={openDesktopModal}
                  >
                    <span className='mr-2 font-heading text-lg font-medium leading-[16px]'>
                      Desktop Wallet
                    </span>
                    <ExportedImage
                      placeholder='empty'
                      src={gradientLinkArrow}
                      className='h-[19px] w-auto pl-[7px]'
                      width={16}
                      height={17}
                      alt='link'
                      priority
                    />
                  </motion.button>
                  <Dialog isOpen={isDesktopModalOpen} onDismiss={closeDesktopModal}>
                    <div className=''>
                      <div className='fixed inset-0 bg-black bg-opacity-70' />
                      <div className='fixed inset-0 overflow-y-auto'>
                        <div className='flex min-h-full items-center justify-center p-4 text-center'>
                          <motion.div
                            ref={desktopModalRef}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className='w-full max-w-md transform overflow-hidden rounded-2xl bg-slate-800 px-6 pb-7 pt-6 text-left align-middle font-sans shadow-xl transition-all sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl 2xl:px-8 2xl:pb-9 2xl:pt-8'
                          >
                            <div className='flex flex-row items-center justify-between'>
                              <h3 className='text-lg font-medium leading-6 text-blue-200 sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl'>
                                Download Desktop Wallet
                              </h3>
                              <button
                                type='button'
                                className='text-blue-200 hover:text-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
                                onClick={closeDesktopModal}
                              >
                                <XCircleIcon className='h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 lg:h-9 lg:w-9 xl:h-10 xl:w-10' />
                              </button>
                            </div>
                            <div className='mt-4 lg:mt-6 xl:mt-8'>
                              <h4 className='mb-4 text-base text-white sm:text-lg md:text-xl lg:text-2xl xl:mb-6 xl:text-3xl'>
                                Choose your operating system
                              </h4>
                              <div className='flex flex-row gap-4 lg:gap-6 xl:gap-8 2xl:gap-10'>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <Link href={KomodoWalletDownloads.windows} key='windows'>
                                    <ExportedImage
                                      placeholder='empty'
                                      src={windows}
                                      alt='windows'
                                      className='h-auto w-[90px] sm:w-[120px] md:w-[147px] lg:w-[170px] xl:w-[190px] 2xl:w-[220px]'
                                      priority
                                    />
                                  </Link>
                                </motion.div>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <Link
                                    href={KomodoWalletDownloads.macos}
                                    key='macos'
                                    className='all-desktop-hero-mac'
                                  >
                                    <ExportedImage
                                      placeholder='empty'
                                      src={macos}
                                      alt='mac-os'
                                      className='h-auto w-[90px] sm:w-[120px] md:w-[147px] lg:w-[170px] xl:w-[190px] 2xl:w-[220px]'
                                      priority
                                    />
                                  </Link>
                                </motion.div>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <Link
                                    href={KomodoWalletDownloads.linux}
                                    key='linux'
                                    className='all-desktop-hero-linux'
                                  >
                                    <ExportedImage
                                      placeholder='empty'
                                      src={linux}
                                      alt='linux'
                                      className='h-auto w-[90px] sm:w-[120px] md:w-[147px] lg:w-[170px] xl:w-[190px] 2xl:w-[220px]'
                                      priority
                                    />
                                  </Link>
                                </motion.div>
                              </div>
                              <p className='mt-5 font-heading text-sm font-normal leading-[110%] text-white md:text-base'>
                                Release Notes, Checksum & VirusTotal Analysis are available on the{' '}
                                <Link
                                  className='text-blue-400 underline'
                                  href={KomodoWalletDownloads.releasePage}
                                  target='_blank'
                                >
                                  release page
                                </Link>
                              </p>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </Dialog>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type='button'
                    className='downloads__desktop-wallet-btn inline-flex h-[58px] w-[250px] items-center justify-center p-[13px] text-center'
                    aria-expanded='false'
                    onClick={openMobileModal}
                  >
                    <span className='mr-2 font-heading text-lg font-medium leading-[16px]'>
                      Mobile Wallet
                    </span>
                    <ExportedImage
                      placeholder='empty'
                      src={gradientLinkArrow}
                      className='h-[19px] w-auto pl-[7px]'
                      width={16}
                      height={17}
                      alt='link'
                      priority
                    />
                  </motion.button>
                  <Dialog isOpen={isMobileModalOpen} onDismiss={closeMobileModal}>
                    <div className=''>
                      <div className='fixed inset-0 bg-black bg-opacity-70' />
                      <div className='fixed inset-0 overflow-y-auto'>
                        <div className='flex min-h-full items-center justify-center p-4 text-center'>
                          <motion.div
                            ref={mobileModalRef}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className='w-full max-w-md transform rounded-2xl bg-slate-800 p-6 text-left align-middle font-sans shadow-xl transition-all sm:p-7 md:max-w-lg md:p-8 lg:max-w-xl lg:p-9 xl:max-w-2xl xl:p-10 2xl:max-w-3xl 2xl:p-12'
                          >
                            <div className='flex flex-row items-center justify-between'>
                              <h3 className='text-lg font-medium leading-6 text-blue-200 sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl'>
                                Download Mobile Wallet
                              </h3>
                              <button
                                type='button'
                                className='text-blue-200 hover:text-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
                                onClick={closeMobileModal}
                              >
                                <XCircleIcon className='h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 lg:h-9 lg:w-9 xl:h-10 xl:w-10' />
                              </button>
                            </div>
                            <div className='mt-4 lg:mt-6 xl:mt-8'>
                              <div className='flex flex-row justify-start gap-4 lg:gap-8 xl:gap-12 2xl:gap-16'>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <Link
                                    href={KomodoWalletDownloads.android}
                                    className='all-mobile-hero-android'
                                  >
                                    <ExportedImage
                                      placeholder='empty'
                                      src={android}
                                      alt='android'
                                      priority
                                      className='h-auto w-[90px] sm:w-[120px] md:w-[150px] lg:w-[180px] xl:w-[210px] 2xl:w-[240px]'
                                    />
                                  </Link>
                                  <div
                                    className='absolute hidden group-hover:block'
                                    style={{
                                      transform: 'translate(-50%, 0)',
                                      bottom: 'calc(70% + 16px)',
                                      left: '50%',
                                    }}
                                  >
                                    <div className='flex w-[150px] flex-col items-center sm:w-[180px] md:w-[196px] lg:w-[220px] xl:w-[250px] 2xl:w-[280px]'>
                                      <ExportedImage
                                        placeholder='empty'
                                        src={androidqr}
                                        alt='qr for downloading komodo wallet on android'
                                        className='h-[90px] w-[90px] sm:h-[100px] sm:w-[100px] md:h-[120px] md:w-[120px] lg:h-[280px] lg:w-[280px]'
                                        priority
                                      />
                                    </div>
                                  </div>
                                </motion.div>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <Link
                                    href={KomodoWalletDownloads.ios}
                                    className='all-mobile-hero-ios'
                                  >
                                    <ExportedImage
                                      placeholder='empty'
                                      src={ios}
                                      alt='ios'
                                      priority
                                      className='h-auto w-[90px] sm:w-[120px] md:w-[150px] lg:w-[180px] xl:w-[210px] 2xl:w-[240px]'
                                    />
                                  </Link>
                                  <div
                                    className='absolute hidden group-hover:block'
                                    style={{
                                      transform: 'translate(-50%, 0)',
                                      bottom: 'calc(70% + 16px)',
                                      left: '50%',
                                    }}
                                  >
                                    <div className='flex w-[150px] flex-col items-center sm:w-[180px] md:w-[196px] lg:w-[220px] xl:w-[250px] 2xl:w-[280px]'>
                                      <ExportedImage
                                        placeholder='empty'
                                        src={iosqr}
                                        alt='qr for downloading komodo wallet on ios'
                                        className='h-[90px] w-[90px] sm:h-[100px] sm:w-[100px] md:h-[120px] md:w-[120px] lg:h-[280px] lg:w-[280px]'
                                        priority
                                      />
                                    </div>
                                  </div>
                                </motion.div>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </Dialog>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type='button'
                    className='downloads__desktop-wallet-btn inline-flex h-[58px] w-[250px] items-center justify-center p-[13px] text-center'
                    aria-expanded='false'
                    onClick={openWebModal}
                  >
                    <span className='mr-2 font-heading text-lg font-medium leading-[16px]'>
                      Web Wallet
                    </span>
                    <ExportedImage
                      placeholder='empty'
                      src={gradientLinkArrow}
                      className='h-[19px] w-auto pl-[7px]'
                      width={16}
                      height={17}
                      alt='link'
                      priority
                    />
                  </motion.button>
                  <Dialog isOpen={isWebModalOpen} onDismiss={closeWebModal}>
                    <div className=''>
                      <div className='fixed inset-0 bg-black bg-opacity-70' />
                      <div className='fixed inset-0 overflow-y-auto'>
                        <div className='flex min-h-full items-center justify-center p-4 text-center'>
                          <motion.div
                            ref={webModalRef}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className='w-full max-w-md transform overflow-hidden rounded-2xl bg-slate-800 px-6 pb-7 pt-6 text-left align-middle font-sans shadow-xl transition-all sm:max-w-lg lg:max-w-xl lg:px-8 lg:pb-9 lg:pt-8 xl:max-w-2xl xl:px-10 xl:pb-10 xl:pt-9 2xl:max-w-3xl'
                          >
                            <div className='flex flex-row items-center justify-between'>
                              <h3 className='text-lg font-medium leading-6 text-blue-200 sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl'>
                                Use Web Wallet
                              </h3>
                              <button
                                type='button'
                                className='text-blue-200 hover:text-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
                                onClick={closeWebModal}
                              >
                                <XCircleIcon className='h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 lg:h-9 lg:w-9 xl:h-10 xl:w-10' />
                              </button>
                            </div>
                            <div className='mt-4 lg:mt-6 xl:mt-8'>
                              <div className='flex flex-col gap-4 lg:gap-6 xl:gap-8'>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <motion.a href={siteData.webDEXlink} className='' target='_blank'>
                                    <motion.button
                                      type='button'
                                      className={`hero-btn relative mx-auto flex w-[200px] flex-row items-center justify-center overflow-hidden rounded-[14px] border border-none px-3 py-2 text-center font-sans text-sm font-bold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-[215px] sm:text-base md:w-[230px] lg:w-[260px] lg:px-4 lg:py-3 lg:text-lg xl:w-[290px] xl:px-5 xl:py-4 xl:text-xl 2xl:w-[320px] 2xl:text-2xl`}
                                      whileHover={{
                                        y: -4,
                                        boxShadow: '0 4px 15px rgba(84, 104, 230, 0.6)',
                                        transition: { duration: 0.2 },
                                      }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <motion.span>Launch Wallet</motion.span>
                                      <ExportedImage
                                        placeholder='empty'
                                        src={gradientLinkArrow}
                                        className='h-[15px] w-auto pl-[7px] sm:h-[17px] md:h-[19px] lg:h-[21px] xl:h-[23px] 2xl:h-[25px]'
                                        width={16}
                                        height={17}
                                        alt='link'
                                      />
                                    </motion.button>
                                  </motion.a>
                                </motion.div>
                                <motion.div
                                  className='group relative flex justify-center'
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ type: 'spring', stiffness: 400 }}
                                >
                                  <Link
                                    href={KomodoWalletDownloads.chromeWebStore}
                                    className='flex w-[200px] flex-wrap items-center justify-center gap-2 rounded-[14px] bg-white px-[15px] py-[12px] text-sm font-[500] text-[#13141a] xms:flex-nowrap sm:w-[215px] sm:text-base md:w-[230px] md:px-[30px] lg:w-[260px] lg:px-[20px] lg:py-[14px] lg:text-lg xl:w-[290px] xl:px-[25px] xl:py-[16px] xl:text-xl 2xl:w-[320px] 2xl:text-2xl'
                                  >
                                    <ChromeColoredIcon className='h-5 w-5 sm:h-5 sm:w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 2xl:h-9 2xl:w-9' />
                                    Install on Chrome
                                  </Link>
                                </motion.div>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </Dialog>
                </motion.div>
              </motion.div>
            </motion.section>
          </div>
          <div className='hidden lg:col-span-2 lg:block'></div>
        </div>
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className='relative pt-[120px]'
        >
          <ExportedImage
            placeholder='empty'
            alt='laptop-mobile'
            className='mx-auto h-auto w-full 3xl:w-[75vw]'
            src={laptopMobile1}
            priority
          />
        </motion.section>
        <div className='container mx-auto -mt-[100px] px-4 sm:px-8 lg:-mt-[200px] lg:grid lg:grid-cols-24 lg:gap-0 xl:-mt-[300px] xl:px-0 3xl:pt-[100px]'>
          <div className='hidden lg:col-span-2 lg:block'></div>
          <div className='relative mx-auto w-full lg:col-span-24 xl:col-span-20 xl:px-0'>
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className='pb-[100px] pt-[120px]'
            >
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className='text-left font-heading text-[40px] font-semibold leading-[48px] tracking-[-0.01em] lg:text-[64px] lg:leading-[76px] lg:tracking-[-0.02em]'>
                  Perfection? Exactly.
                </h2>
                <p className='pt-1 text-left font-heading text-lg font-normal'>
                  Here&apos;s how Komodo Wallet compares to the competition.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className='w-full pt-[25px]'
              >
                <WalletComparison />
              </motion.div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className='downloads__faq-bg py-[50px] text-white lg:py-[96px]'
            >
              <div className='relative z-10 mx-auto px-5'>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className='text-center text-[2em] leading-[1.3] tracking-[-0.01em] md:text-[2.25em] md:leading-[1.4]'
                >
                  Frequently Asked Questions
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className='pb-8 pt-3 text-center font-heading text-[20px] font-normal leading-[32px] tracking-[0.01em] text-[#E4E7EC] lg:pt-5'
                >
                  Everything you need to know about Komodo Wallet.
                </motion.p>

                <motion.div
                  variants={staggerContainer}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                >
                  {faqData.map((item, index) => (
                    <motion.div
                      key={index}
                      variants={fadeInUp}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <FAQ
                        faqNo={item.faqNo}
                        faqQstn={item.faqQstn}
                        faqAnswer={item.faqAnswer}
                        className1='bg-[#182230] bg-opacity-100'
                        className='rounded-[16px] bg-opacity-0'
                        selected={selected}
                        setSelected={setSelected}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className='flex flex-col items-center justify-center pt-[40px] text-center'
                >
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className='rounded-[16px] bg-[#051019] px-[32px] pb-[40px] pt-[32px] 2xl:w-[1152px]'
                  >
                    <h4 className='pb-2 font-heading text-xl font-semibold leading-[32px] tracking-[0.01em] text-white'>
                      Still have questions?
                    </h4>
                    <p className='pb-6 text-center font-heading text-lg font-normal leading-[30px] tracking-[0.01em] text-[#E4E7EC]'>
                      Can&apos;t find the answer you&apos;re looking for? Please chat to our
                      friendly team.
                    </p>
                    <Link className='' href='/en/contact/'>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type='button'
                        className='downloads__desktop-wallet-btn inline-flex h-auto w-[150px] items-center justify-center rounded-[5px] p-[13px] px-6 py-3 text-center'
                        aria-expanded='false'
                      >
                        <span className='font-sans text-base font-semibold leading-5 text-white'>
                          Get in touch
                        </span>
                      </motion.button>
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </motion.section>
          </div>
          <div className='hidden lg:col-span-2 lg:block'></div>
        </div>
      </main>
    </>
  );
}
